import React, { useState, useEffect } from 'react';
import api, { getImageUrl } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaGripVertical, FaSave } from 'react-icons/fa';
import SortableList from '../components/SortableList';

const emptySlider = {
  title: '',
  subtitle: '',
  button_text: '',
  button_link: '',
  sort_order: 0,
  is_active: true,
};

export default function AdminSliders() {
  useAuth();
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptySlider });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const res = await api.get('/sliders/all');
      setSliders(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Sliderlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptySlider });
    setImage(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEdit = (slider) => {
    setEditing(slider);
    setForm({
      title: slider.title || '',
      subtitle: slider.subtitle || '',
      button_text: slider.button_text || '',
      button_link: slider.button_link || '',
      sort_order: slider.sort_order || 0,
      is_active: slider.is_active !== undefined ? slider.is_active : true,
    });
    setImage(null);
    setImagePreview(slider.image_path ? getImageUrl(slider.image_path) : null);
    setShowModal(true);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value);
      });
      if (image) {
        formData.append('image', image);
      } else if (editing && editing.image_path) {
        formData.append('existing_image', editing.image_path);
      }

      if (editing) {
        await api.put(`/sliders/${editing.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Slider güncellendi');
      } else {
        await api.post('/sliders', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Slider eklendi');
      }
      setShowModal(false);
      fetchSliders();
    } catch (err) {
      toast.error('İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = (newSliders) => {
    setSliders(newSliders);
    setOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    try {
      const ids = sliders.map((s) => s.id);
      await api.put('/sliders/reorder', { ids });
      toast.success('Siralama kaydedildi');
      setOrderChanged(false);
      fetchSliders();
    } catch (err) {
      toast.error('Siralama kaydedilirken hata olustu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu slideri silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/sliders/${id}`);
      toast.success('Slider silindi');
      fetchSliders();
    } catch (err) {
      toast.error('Silme sırasında hata oluştu');
    }
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Slider Yönetimi</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            {orderChanged && (
              <button className="btn btn-success" onClick={handleSaveOrder}>
                <FaSave /> Siralamayi Kaydet
              </button>
            )}
            <button className="btn btn-primary" onClick={openAdd}>
              <FaPlus /> Yeni Slider
            </button>
          </div>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Görsel</th>
              <th>Başlık</th>
              <th>Alt Başlık</th>
              <th>Sira</th>
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {sliders.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>
                  Henüz slider eklenmemiş
                </td>
              </tr>
            ) : (
              <SortableList
                items={sliders}
                onReorder={handleReorder}
                renderItem={({ item: slider, ref, style, attributes, listeners }) => (
                  <tr key={slider.id} ref={ref} style={style} className="sortable-row">
                    <td>
                      <span className="sortable-drag-handle" {...attributes} {...listeners}>
                        <FaGripVertical />
                      </span>
                    </td>
                    <td>
                      {slider.image_path && (
                        <img src={getImageUrl(slider.image_path)} alt={slider.title} />
                      )}
                    </td>
                    <td>{slider.title}</td>
                    <td>{slider.subtitle}</td>
                    <td>{slider.sort_order}</td>
                    <td>
                      <span className={`badge ${slider.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {slider.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button className="btn btn-primary btn-sm" onClick={() => openEdit(slider)}>
                          <FaEdit />
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(slider.id)}>
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              />
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editing ? 'Slider Düzenle' : 'Yeni Slider'}</h2>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group">
                <label>Başlık</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Alt Başlık</label>
                <input
                  type="text"
                  value={form.subtitle}
                  onChange={(e) => handleChange('subtitle', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Buton Metni</label>
                <input
                  type="text"
                  value={form.button_text}
                  onChange={(e) => handleChange('button_text', e.target.value)}
                  placeholder="Örn: Bizi Arayın, Ücretsiz Keşif"
                />
              </div>
              <div className="form-group">
                <label>Buton Linki</label>
                <input
                  type="text"
                  value={form.button_link}
                  onChange={(e) => handleChange('button_link', e.target.value)}
                  placeholder="Örn: /iletisim, tel:+905551234567"
                />
                <small style={{color:'#999',fontSize:'0.8rem',marginTop:4,display:'block'}}>
                  Sayfa: /iletisim, /hizmetler | Telefon: tel:+90... | Dış link: https://...
                </small>
              </div>
              <div className="form-group">
                <label>Sira</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => handleChange('sort_order', parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="form-group">
                <label>Durum</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                  <span>{form.is_active ? 'Aktif' : 'Pasif'}</span>
                </div>
              </div>
              <div className="form-group full-width">
                <label>Görsel</label>
                <input type="file" accept="image/*" onChange={handleImageChange} />
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                )}
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
